import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Plus, ArrowUpCircle, ArrowDownCircle, History, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (amount, currency = 'USD') => {
  if (currency === 'USD') return `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'EUR') return `€${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'GBP') return `£${Number(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${Number(amount || 0).toLocaleString('en-IN')}`;
};

const LedgerPage = () => {
  const { user } = useAuth();
  const [ledgers, setLedgers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(null);
  const [showTxModal, setShowTxModal] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [form, setForm] = useState({ user_id: '', user_name: '', account_type: 'CARD', currency: 'USD', balance: 0 });
  const [adjForm, setAdjForm] = useState({ amount: 0, description: '', type: 'credit' });
  const [error, setError] = useState('');

  const canManage = ['admin', 'accounts'].includes(user?.role);
  const isBdm = user?.role === 'bdm';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const params = isBdm ? { user_id: user.id } : {};
      const [ledgerRes, usersRes] = await Promise.all([
        axios.get(`${API}/ledger`, { params }),
        canManage ? axios.get(`${API}/users`) : Promise.resolve({ data: [] })
      ]);
      setLedgers(ledgerRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/ledger`, form);
      setShowCreateModal(false);
      setForm({ user_id: '', user_name: '', account_type: 'CARD', currency: 'USD', balance: 0 });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error creating ledger');
    }
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const endpoint = adjForm.type === 'credit' ? 'credit' : 'debit';
      await axios.post(`${API}/ledger/${showAdjustModal}/${endpoint}`, {
        amount: Number(adjForm.amount),
        description: adjForm.description
      });
      setShowAdjustModal(null);
      setAdjForm({ amount: 0, description: '', type: 'credit' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error adjusting balance');
    }
  };

  const viewTransactions = async (ledgerId) => {
    try {
      const res = await axios.get(`${API}/ledger/${ledgerId}/transactions`);
      setTransactions(res.data);
      setShowTxModal(ledgerId);
    } catch (err) {
      console.error(err);
    }
  };

  const handleUserSelect = (userId) => {
    const selected = users.find(u => u.id === userId);
    setForm(prev => ({ ...prev, user_id: userId, user_name: selected?.name || '' }));
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading...</div></div>;

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Company Ledger
          </h1>
          <p className="text-sm text-gray-500 mt-1">International expense accounts (Card & Cash balances)</p>
        </div>
        {canManage && (
          <button onClick={() => setShowCreateModal(true)} className="btn-primary flex items-center gap-2" data-testid="create-ledger-btn">
            <Plus className="w-4 h-4" /> New Ledger
          </button>
        )}
      </div>

      {ledgers.length === 0 ? (
        <div className="card text-center py-12">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No ledger accounts found</p>
          {canManage && <p className="text-gray-400 text-xs mt-1">Create a ledger to track international expense balances</p>}
        </div>
      ) : (
        <div className="grid gap-4">
          {ledgers.map(ledger => (
            <div key={ledger.id} className="card flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`ledger-card-${ledger.id}`}>
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ledger.account_type === 'CARD' ? 'bg-blue-50' : 'bg-amber-50'}`}>
                  <BookOpen className={`w-5 h-5 ${ledger.account_type === 'CARD' ? 'text-blue-600' : 'text-amber-600'}`} />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{ledger.user_name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ledger.account_type === 'CARD' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{ledger.account_type}</span>
                    <span className="text-xs text-gray-400">{ledger.currency}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-xs text-gray-400 uppercase">Balance</div>
                  <div className={`text-lg font-bold ${ledger.balance > 0 ? 'text-emerald-600' : ledger.balance === 0 ? 'text-gray-500' : 'text-red-600'}`}>
                    {formatCurrency(ledger.balance, ledger.currency)}
                  </div>
                </div>
                <div className="flex gap-1">
                  {canManage && (
                    <button onClick={() => { setShowAdjustModal(ledger.id); setError(''); }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Adjust Balance" data-testid={`adjust-${ledger.id}`}>
                      <ArrowUpCircle className="w-4 h-4 text-gray-500" />
                    </button>
                  )}
                  <button onClick={() => viewTransactions(ledger.id)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="View Transactions" data-testid={`txn-${ledger.id}`}>
                    <History className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Ledger Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="create-ledger-modal">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-900">Create Ledger Account</h2>
              <button onClick={() => setShowCreateModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            {error && <div className="text-red-600 text-sm bg-red-50 p-2 rounded mb-3" data-testid="ledger-error">{error}</div>}
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                <select value={form.user_id} onChange={e => handleUserSelect(e.target.value)} required className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="ledger-user-select">
                  <option value="">Select employee...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                  <select value={form.account_type} onChange={e => setForm(p => ({ ...p, account_type: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="ledger-account-type">
                    <option value="CARD">Card</option>
                    <option value="CASH">Cash</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select value={form.currency} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="ledger-currency">
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Initial Balance</label>
                <input type="number" min="0" step="0.01" value={form.balance} onChange={e => setForm(p => ({ ...p, balance: Number(e.target.value) }))} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="ledger-balance" />
              </div>
              <button type="submit" className="btn-primary w-full" data-testid="create-ledger-submit">Create Ledger</button>
            </form>
          </div>
        </div>
      )}

      {/* Adjust Balance Modal */}
      {showAdjustModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAdjustModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="adjust-ledger-modal">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-900">Adjust Balance</h2>
              <button onClick={() => setShowAdjustModal(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            {error && <div className="text-red-600 text-sm bg-red-50 p-2 rounded mb-3">{error}</div>}
            <form onSubmit={handleAdjust} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setAdjForm(p => ({ ...p, type: 'credit' }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${adjForm.type === 'credit' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'border-gray-200 text-gray-500'}`}>
                    <ArrowUpCircle className="w-4 h-4 inline mr-1" />Credit (Add)
                  </button>
                  <button type="button" onClick={() => setAdjForm(p => ({ ...p, type: 'debit' }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${adjForm.type === 'debit' ? 'bg-red-50 border-red-300 text-red-700' : 'border-gray-200 text-gray-500'}`}>
                    <ArrowDownCircle className="w-4 h-4 inline mr-1" />Debit (Remove)
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input type="number" min="0.01" step="0.01" value={adjForm.amount} onChange={e => setAdjForm(p => ({ ...p, amount: e.target.value }))} required className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="adjust-amount" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={adjForm.description} onChange={e => setAdjForm(p => ({ ...p, description: e.target.value }))} placeholder="Reason for adjustment" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="adjust-description" />
              </div>
              <button type="submit" className={`w-full py-2.5 rounded-lg text-sm font-semibold text-white ${adjForm.type === 'credit' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`} data-testid="adjust-submit">
                {adjForm.type === 'credit' ? 'Add Funds' : 'Remove Funds'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Transactions Modal */}
      {showTxModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowTxModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="transactions-modal">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-bold text-gray-900">Transaction History</h2>
              <button onClick={() => setShowTxModal(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="overflow-y-auto max-h-[60vh] p-4">
              {transactions.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No transactions</p>
              ) : (
                <div className="space-y-2">
                  {transactions.map(tx => (
                    <div key={tx.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`tx-${tx.id}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          {tx.transaction_type === 'credit' ? (
                            <ArrowUpCircle className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <ArrowDownCircle className="w-4 h-4 text-red-500" />
                          )}
                          <span className="text-sm font-medium text-gray-900">{tx.description || tx.reference_type}</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">{new Date(tx.created_at).toLocaleString()} by {tx.created_by}</div>
                      </div>
                      <div className={`font-bold text-sm ${tx.transaction_type === 'credit' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {tx.transaction_type === 'credit' ? '+' : '-'}{Number(tx.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LedgerPage;
